/**
 * The anonymization engine inside Pyodide, shared by the browser worker and the Node integration test.
 * Callers provide the Pyodide instance and the engine files; this module wires the Python bridge
 * to the JPEG Lossless codec.
 */
import bridgeSource from './bridge.py?raw'
import { decodeJpegLossless, encodeJpegLossless } from '../codec/jpegLossless'
import type { Label } from '../lib/types'

/** Minimal surface of Pyodide used here. */
export interface Pyodide {
  FS: { mkdirTree(path: string): void; writeFile(path: string, data: Uint8Array | string): void }
  runPython(code: string): unknown
  pyimport(name: string): PyBridge
  toPy(value: unknown): unknown
}

interface PyProxy {
  toJs(): unknown
  destroy(): void
}

interface PyBridge {
  configure(token: string, site: string | null, catalog: string | null): string | undefined
  pseudonym(token: string, site: string | null, patientId: string): string
  survey(source: Uint8Array): string
  analyze(source: Uint8Array, overrides: string | null, present: string | null): string
  pixel_key(source: Uint8Array): string
  process(source: Uint8Array, encode: Encoder, overrides: string | null, targets?: Uint8Array[], targetOverrides?: string): PyProxy
  viewable(source: Uint8Array, encode: Encoder): PyProxy
}

type Encoder = (samples: PyProxy, columns: number, rows: number, precision: number, bytesPerSample: number) => Uint8Array

/** Engine files keyed by their path inside packages/pedfx, e.g. `anonymization/pedfx/core.py`. */
export type EngineFiles = Record<string, Uint8Array>

export interface EngineError {
  code: string
  field: string | null
  message: string
}

export interface Analysis {
  /** Anonymized before, by the platform or the standalone app (ANON-08). */
  recognized: boolean
  keyId: string
  /** The file states burned-in annotation (0028,0301), which may be identifying text (ANON-11). */
  burnedIn: boolean
  patient: string | null
  study: string
  /** The file's name within an upload, for references between its files (keyed, never an original UID). */
  instance?: string | null
  /** The instances it refers to, named alike. */
  refers?: string[]
  /** The Issuer of Patient ID and the institution the file names, compared across an upload (TOK-14); never sent. */
  issuer: string
  institution: string
  /** What the identity check compares across the upload (TOK-32), keyed with the token; null for files in the platform's format. Never sent. */
  identity: IdentityEvidence | null
  issues: { code: string; field: string | null; level: 'block' | 'warning' | string; message: string }[]
  /** The kinds of annotation the label catalog lacks that the label map leaves open (UPL-09). */
  foreign: Foreign[]
}

/**
 * An original's identity as the identity check compares it (TOK-32): its source UIDs, name, birth date
 * and patient ID without leading zeros, each keyed with the token, or null where the file states none.
 */
export interface IdentityEvidence {
  study: string | null
  series: string | null
  instance: string | null
  name: string | null
  birthDate: string | null
  unpadded: string | null
}

/** What a site check counts of one original (TOK-30); the patient ID never leaves the engine. */
export interface Survey {
  issuer: string
  institution: string
  /** Timezone Offset From UTC as the file states it. */
  timeZone: string | null
  lacks: ('side' | 'bodyPartTerm' | 'viewPosition')[]
}

/** Annotations of a label the catalog lacks, or has for another shape type, in one file. */
export interface Foreign {
  type: Label['type']
  label: string
  count: number
}

/** `type:label`, the key of a foreign kind of annotation in the label map. */
export const foreignKey = ({ type, label }: Pick<Foreign, 'type' | 'label'>) => `${type}:${label}`

/** A catalog label of the annotations' shape type with attribute values, or null to leave them out. */
export type LabelTarget = { label: string; attributes?: Record<string, string> } | null

export interface Facts {
  filename: string
  keyId: string
  contentDigest: string
  dedupKey: string
  /** The file brings annotation sets (FMT-07). */
  annotated: boolean
  preview: Preview
}

/** What the anonymized file keeps, shown before upload (ANON-07). */
export interface Preview {
  /** Height in metres and weight in kilograms, where the file keeps them (TOK-16). */
  height: number | null
  weight: number | null
  /** The free text its annotation sets carry: authors and attribute values (ANON-14). */
  texts: string[]
  patient: string
  /** The shifted acquisition time. */
  acquiredMillis: number
  ageTenths: number | null
  sex: string | null
  bodyPart: string | null
  side: string | null
  view: string | null
}

/** The site's time zone (TOK-29), which new originals need. */
export interface SiteConfig {
  time_zone: string
}

/** What the engine anonymizes new originals with: the site token and the site's configuration. */
export interface Setup {
  token: string
  site: SiteConfig | null
  /** The platform's label catalog, which annotations must fit (LBL-01); without it, the engine's bundled list. */
  labels?: readonly Label[]
}

/**
 * Values the uploader corrects or supplies before anonymizing (ANON-06), in the engine's terms.
 * They stay in the browser: the patient ID only feeds the pseudonym.
 */
export interface Overrides {
  patientId?: string
  side?: 'L' | 'R' | 'B' | 'U'
  bodyPartTerm?: string
  /** What the annotations of each foreign kind become, by `foreignKey` (UPL-09). */
  labelMap?: Record<string, LabelTarget>
  /** The decision to omit references to images that were never anonymized, e.g. a stitched image's sources. */
  omitReferences?: boolean
}

/** An original of the upload another file refers to, with its corrections (References). */
export interface Target<Bytes = Uint8Array> {
  source: Bytes
  overrides?: Overrides
}

export type Result<T> = { ok: true; value: T } | { ok: false; error: EngineError }

export interface Engine {
  /** Keeps the setup and returns its key ID; an empty token clears it (UPL-18). */
  configure(setup: Setup): string | null
  /** A patient's pseudonym from the original patient ID as typed; one character repeated has none (TOK-21, TOK-32). */
  pseudonym(setup: Setup, patientId: string): Result<string>
  /** What a site check counts of one original, without a token (TOK-30). */
  survey(source: Uint8Array): Result<Survey>
  /** `present`: the instances of the upload the file's references resolve to (`Analysis.refers`). */
  analyze(source: Uint8Array, overrides?: Overrides, present?: string[]): Result<Analysis>
  /** The duplicate key of an original's decoded pixels, without a token (TOK-21). */
  pixelKey(source: Uint8Array): Result<string>
  /** `targets`: the originals of the upload the file refers to, which its references resolve to. */
  process(source: Uint8Array, overrides?: Overrides, targets?: Target[]): Result<{ data: Uint8Array; facts: Facts }>
  /** The original with JPEG Lossless pixel data, for showing a file held back; not anonymized. */
  viewable(source: Uint8Array): Result<Uint8Array>
}

const ROOT = '/pedfx'

const encode: Encoder = (samples, columns, rows, precision, bytesPerSample) => {
  const bytes = samples.toJs() as Uint8Array
  const values = bytesPerSample === 2 ? new Uint16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2) : bytes
  return encodeJpegLossless(values, columns, rows, precision)
}

/** Called by the engine for JPEG Lossless sources (pedfx/engine.py `decode_pixels`). */
function pedfxDecodeLossless(frame: PyProxy, bytesPerSample: number, rows: number, columns: number): Uint8Array {
  const { width, height, samples } = decodeJpegLossless(frame.toJs() as Uint8Array)
  if (width !== columns || height !== rows) throw new Error('JPEG dimensions do not match the image')
  return bytesPerSample === 1 ? Uint8Array.from(samples) : new Uint8Array(samples.buffer)
}

/** A plain Uint8Array view; Pyodide rejects subclasses such as Node's Buffer. */
function plain(bytes: Uint8Array): Uint8Array {
  return bytes.constructor === Uint8Array ? bytes : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

const asJson = (overrides?: Overrides) => (overrides && Object.keys(overrides).length ? JSON.stringify(overrides) : null)

function parse<T>(json: string): Result<T> {
  const value = JSON.parse(json) as T | { error: EngineError }
  return value !== null && typeof value === 'object' && 'error' in value ? { ok: false, error: value.error } : { ok: true, value }
}

export function startEngine(pyodide: Pyodide, files: EngineFiles): Engine {
  for (const [path, data] of Object.entries(files)) {
    const target = `${ROOT}/${path}`
    pyodide.FS.mkdirTree(target.slice(0, target.lastIndexOf('/')))
    pyodide.FS.writeFile(target, data)
  }
  pyodide.FS.mkdirTree(`${ROOT}/bridge`)
  pyodide.FS.writeFile(`${ROOT}/bridge/bridge.py`, bridgeSource)
  Object.assign(globalThis, { pedfxDecodeLossless })
  pyodide.runPython(
    `import sys\nsys.path[:0] = ['${ROOT}/anonymization', '${ROOT}/adapter', '${ROOT}/bridge']\n` +
      'import pedfx_adapter\npedfx_adapter.check_artifacts()',
  )
  const bridge = pyodide.pyimport('bridge')

  return {
    configure: ({ token, site, labels }) => bridge.configure(token, site && JSON.stringify(site), labels ? JSON.stringify(labels) : null) ?? null,
    pseudonym: ({ token, site }, patientId) => parse<string>(bridge.pseudonym(token, site && JSON.stringify(site), patientId)),
    survey: (source) => parse<Survey>(bridge.survey(plain(source))),
    analyze: (source, overrides, present) =>
      parse<Analysis>(bridge.analyze(plain(source), asJson(overrides), present?.length ? JSON.stringify(present) : null)),
    pixelKey: (source) => parse<string>(bridge.pixel_key(plain(source))),
    process: (source, overrides, targets) => {
      const result = bridge.process(
        plain(source),
        encode,
        asJson(overrides),
        targets?.length ? targets.map((target) => plain(target.source)) : undefined,
        targets?.length ? JSON.stringify(targets.map((target) => asJson(target.overrides))) : undefined,
      )
      try {
        const [data, json] = result.toJs() as [Uint8Array | undefined, string]
        const parsed = parse<Facts>(json)
        return parsed.ok ? { ok: true, value: { data: data!, facts: parsed.value } } : parsed
      } finally {
        result.destroy()
      }
    },
    viewable: (source) => {
      const result = bridge.viewable(plain(source), encode)
      try {
        const [data, error] = result.toJs() as [Uint8Array | undefined, string | undefined]
        return data ? { ok: true, value: data } : parse<Uint8Array>(error!)
      } finally {
        result.destroy()
      }
    },
  }
}
